import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api, { ensureCsrf } from './utils/api';

function CreateLeadNew() {
  const navigate = useNavigate();
  const [jobRoleSearch, setJobRoleSearch] = useState('');
  const [jobRoleResults, setJobRoleResults] = useState([]);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [roleSelected, setRoleSelected] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    targetRoleId: '',
    yearsExp: '',
    alternateJobRoles: '',
    location: '',
    country: 'United States of America',
    workPreference: 'All',
    workAuthorization: 'Citizen',
    gender: '',
    expectedSalaryRange: '',
    githubUrl: '',
    google_drive_resume_link: '',
    willingToRelocate: 'Yes',
    sponsorship: '',
    resume: null,
    personalDetails: null,
    number_of_applications: '',
  notes: '',
  // Backend required fields not in initial UI
  plan: 'Standard',
  status: 'In Progress',
  startDate: new Date().toISOString().slice(0,10),
  servicesOpted: JSON.stringify({}),
  excludeCompanies: JSON.stringify([])
  });
  const [fileStatus, setFileStatus] = useState({ resume: 'idle', personalDetails: 'idle' }); // idle|loading|success|error
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state for form submission
  const [errorModal, setErrorModal] = useState(null); // { title, message }

  const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const thresh = 1024;
    if (bytes < thresh) return bytes + ' B';
    const units = ['KB','MB','GB'];
    let u = -1; let size = bytes;
    do { size /= thresh; ++u; } while (size >= thresh && u < units.length - 1);
    return size.toFixed(1) + ' ' + units[u];
  };

  const FileUploadBox = ({ label, name, file, status, accept, onSelect, onRemove, helper }) => {
    const [dragging, setDragging] = useState(false);
    const borderColor = status === 'success' ? 'border-green-400' : status === 'error' ? 'border-red-400' : dragging ? 'border-indigo-400' : 'border-gray-300';
    const bgColor = status === 'success' ? 'bg-green-50' : status === 'error' ? 'bg-red-50' : 'bg-white';
    const dashed = status === 'success' ? '' : 'border-dashed';
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div
          className={`relative flex flex-col items-center justify-center px-6 py-10 border-2 ${dashed} rounded-xl transition-colors duration-300 ${borderColor} ${bgColor}`}
          onDragOver={(e)=>{e.preventDefault(); setDragging(true);}}
          onDragLeave={(e)=>{e.preventDefault(); setDragging(false);}}
          onDrop={(e)=>{e.preventDefault(); setDragging(false); if(e.dataTransfer.files?.[0]) onSelect(name, e.dataTransfer.files[0]);}}
        >
          {!file && (
            <>
              <svg className="mx-auto h-14 w-14 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4M32 8h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="mt-4 text-sm text-gray-700"><span className="text-indigo-600 font-medium cursor-pointer relative">
                <input
                  type="file"
                  accept={accept}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e)=> e.target.files && onSelect(name, e.target.files[0])}
                />Upload a file</span> or drag and drop</p>
              <p className="mt-2 text-xs text-gray-500">{helper}</p>
            </>
          )}
          {file && (
            <>
              <div className="flex flex-col items-center">
                <div className="h-20 w-20 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
                  <svg className="h-10 w-10 text-indigo-600" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"/><path d="M14 3.5V9h5.5" fill="#fff" opacity="0.6"/></svg>
                </div>
                <p className="text-base font-medium text-gray-900 text-center break-all max-w-xs">{file.name}</p>
                <p className="mt-1 text-sm text-gray-500">{formatSize(file.size)}</p>
                <span className={`mt-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${status==='success'?'bg-green-200 text-green-800':status==='error'?'bg-red-200 text-red-800':'bg-indigo-200 text-indigo-800'}`}>
                  {status==='success' && '✅ Ready to upload'}
                  {status==='error' && '⚠️ Invalid file'}
                  {status==='loading' && '⏳ Checking...'}
                  {status==='idle' && 'File selected'}
                </span>
                <button type="button" onClick={()=> onRemove(name)} className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 shadow-sm">
                  <span role="img" aria-label="remove">🗑️</span> Remove File
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Debounced server search for job roles when user types
  useEffect(() => {
    if (!jobRoleSearch || jobRoleSearch.length < 3 || roleSelected) {
      setJobRoleResults([]);
      setShowRoleDropdown(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/v1/jobroles/?search=${encodeURIComponent(jobRoleSearch)}&page_size=20`);
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setJobRoleResults(data);
        setShowRoleDropdown(true);
      } catch (err) {
        setJobRoleResults([]);
        setShowRoleDropdown(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [jobRoleSearch, roleSelected]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const selectFile = useCallback((name, file) => {
    console.log('File selected', name, file);
    if (!file) return;    
    // Basic validation
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const tooBig = file.size > 10 * 1024 * 1024;
    setFileStatus(prev => ({ ...prev, [name]: 'loading' }));
    setFormData(prev => ({
      ...prev,
      [name]: file
    }));
    // Simulate async prep (since actual upload occurs on submit)
    setTimeout(() => {
      if (tooBig || (file.type && !allowed.includes(file.type))) {
        setFileStatus(prev => ({ ...prev, [name]: 'error' }));
      } else {
        setFileStatus(prev => ({ ...prev, [name]: 'success' }));
      }
    }, 400);
  }, []);

  const removeFile = (name) => {
    setFormData(prev => ({ ...prev, [name]: null }));
    setFileStatus(prev => ({ ...prev, [name]: 'idle' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Start loading
    
    // Validate that a target role is selected
    if (!formData.targetRoleId) {
      setErrorModal({ title: 'Validation Error', message: 'Please select a target role.' });
      setIsSubmitting(false);
      return;
    }
    
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      const value = formData[key];
      if (value !== null && value !== undefined && value !== '') {
        if (key === 'alternateJobRoles') {
          // Convert comma separated to JSON list expected by backend
            const arr = value.split(',').map(v=>v.trim()).filter(Boolean);
            formDataToSend.append('alternateJobRoles', JSON.stringify(arr));
        } else if (key === 'targetRoleId') {
            formDataToSend.append('target_role_id', value); // serializer expects target_role_id write-only
        } else {
            formDataToSend.append(key, value);
        }
      }
    });

    try {
      await ensureCsrf();
      await api.post('/api/v1/leads/', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      navigate('/leads'); // Redirect to leads list after successful creation
    } catch (error) {
      console.error('Error submitting form:', error);
      let message = 'Failed to create lead. Please try again.';
      if (error.response) {
        if (error.response.data) {
          // DRF typical error shape { field: ["msg"] } or { detail: "..." }
          const data = error.response.data;
            if (data.email) {
              message = Array.isArray(data.email) ? data.email.join(' ') : data.email;
            } else if (data.non_field_errors) {
              message = Array.isArray(data.non_field_errors) ? data.non_field_errors.join(' ') : data.non_field_errors;
            } else if (data.detail) {
              message = data.detail;
            } else {
              // Flatten other field errors
              const parts = Object.entries(data).map(([k,v]) => `${k}: ${Array.isArray(v)? v.join(' '): v}`);
              if (parts.length) message = parts.join('\n');
            }
        }
      } else if (error.message) {
        message = error.message;
      }
      setErrorModal({ title: 'Unable to Create Lead', message });
    } finally {
      setIsSubmitting(false); // Stop loading regardless of success or error
    }
  };

  const showNewRoleModal = () => {
    // TODO: Implement new role modal
    console.log('New role modal not implemented yet');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Leads</h1>
  <button onClick={() => navigate('/leads')} className="text-gray-600 hover:text-gray-900">Cancel</button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-medium mb-2">Create New Lead</h2>
        <p className="text-gray-600 mb-6">Add a new lead to the system.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <label htmlFor="targetRole" className="block text-sm font-medium text-gray-700">Target Role *</label>
                  <div className="mt-1 relative">
                    <div className="flex rounded-md">
                      <input
                        type="text"
                        name="targetRoleSearch"
                        id="targetRole"
                        placeholder="Type to search roles (3+ chars)..."
                        value={jobRoleSearch}
                        onChange={(e) => { 
                          const v = e.target.value; 
                          setJobRoleSearch(v); 
                          setRoleSelected(false); // Allow dropdown to show again when typing
                          /* clear selection when typing new search */ 
                          setFormData(f=> ({...f, targetRoleId: ''})); 
                          setSelectedRoleName(''); 
                        }}
                        className="mt-1 block flex-1 rounded-l-md border border-gray-300 py-2 px-3 w-full shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={showNewRoleModal}
                        className="mt-1 inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        New Role
                      </button>
                    </div>
                    {showRoleDropdown && (
                      <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
                        {jobRoleResults.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500">No role matches</div>
                        ) : jobRoleResults.map(r => (
                          <button key={r.id} type="button" onClick={() => {
                            setFormData(f => ({ ...f, targetRoleId: String(r.id) }));
                            setSelectedRoleName(r.name);
                            setJobRoleSearch(r.name);
                            setRoleSelected(true); // Mark as selected to prevent dropdown
                            setShowRoleDropdown(false);
                          }} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">{r.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedRoleName && (
                    <div className="mt-2 text-sm text-gray-700">Selected: <span className="font-medium">{selectedRoleName}</span></div>
                  )}
                  {/* Hidden input for form submission */}
                  <input type="hidden" name="targetRoleId" value={formData.targetRoleId} />
                </div>

                <div className="w-32">
                  <label htmlFor="yearsExp" className="block text-sm font-medium text-gray-700">Years of Experience</label>
                  <input
                    type="number"
                    name="yearsExp"
                    id="yearsExp"
                    min="0"
                    required
                    value={formData.yearsExp}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="alternateJobRoles" className="block text-sm font-medium text-gray-700">
                  Alternate Job Roles (optional)
                </label>
                <input
                  type="text"
                  name="alternateJobRoles"
                  id="alternateJobRoles"
                  placeholder="Enter roles separated by commas"
                  value={formData.alternateJobRoles}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  name="location"
                  id="location"
                  required
                  value={formData.location}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                  <select
                    name="country"
                    id="country"
                    required
                    value={formData.country}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="United States of America">United States of America</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="workPreference" className="block text-sm font-medium text-gray-700">Work Preference</label>
                  <select
                    name="workPreference"
                    id="workPreference"
                    required
                    value={formData.workPreference}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="On-Site">On-Site</option>
                    <option value="All">All</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="workAuthorization" className="block text-sm font-medium text-gray-700">Work Authorization</label>
                  <select
                    name="workAuthorization"
                    id="workAuthorization"
                    required
                    value={formData.workAuthorization}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="Citizen">Citizen</option>
                    <option value="Green Card">Green Card</option>
                    <option value="H1B">H1B</option>
                    <option value="F1">F1</option>
                    <option value="H4EAD">H4EAD</option>
                    <option value="Post-Study Work Visa">Post-Study Work Visa</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Gender</label>
                  <select
                    name="gender"
                    id="gender"
                    required
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="expectedSalaryRange" className="block text-sm font-medium text-gray-700">Expected Salary Range</label>
                <input
                  type="text"
                  name="expectedSalaryRange"
                  id="expectedSalaryRange"
                  placeholder="e.g., $80,000 - $120,000"
                  value={formData.expectedSalaryRange}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="number_of_applications" className="block text-sm font-medium text-gray-700">Number of Applications</label>
                <select
                  name="number_of_applications"
                  id="number_of_applications"
                  value={formData.number_of_applications}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">Select Number of Applications</option>
                  <option value="20+">20+</option>
                  <option value="40+">40+</option>
                </select>
              </div>

              <div>
                <label htmlFor="willingToRelocate" className="block text-sm font-medium text-gray-700">Willing to Relocate?</label>
                <select
                  name="willingToRelocate"
                  id="willingToRelocate"
                  value={formData.willingToRelocate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              <div>
                <label htmlFor="sponsorship" className="block text-sm font-medium text-gray-700">Sponsorship Required?</label>
                <select
                  name="sponsorship"
                  id="sponsorship"
                  value={formData.sponsorship}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">Select Option</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              <div>
                <label htmlFor="githubUrl" className="block text-sm font-medium text-gray-700">GitHub URL</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <input
                    type="url"
                    name="githubUrl"
                    id="githubUrl"
                    value={formData.githubUrl}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="https://github.com/username"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="google_drive_resume_link" className="block text-sm font-medium text-gray-700">Google Drive Resume Link</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.01 2C6.5 2 2.01 6.5 2.01 12s4.49 10 9.99 10c1.28 0 2.5-.26 3.64-.74-.74-.78-1.26-1.76-1.49-2.86-.38.08-.78.12-1.15.12-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7c0 .37-.04.77-.12 1.15 1.1.23 2.08.75 2.86 1.49.48-1.14.74-2.36.74-3.64 0-5.5-4.5-10-10-10z"/>
                      <path d="M9 17l-4-4 1.4-1.4L9 14.2l7.6-7.6L18 8l-9 9z"/>
                    </svg>
                  </div>
                  <input
                    type="url"
                    name="google_drive_resume_link"
                    id="google_drive_resume_link"
                    value={formData.google_drive_resume_link}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="https://drive.google.com/file/d/..."
                  />
                </div>
              </div>

              <FileUploadBox
                label="Resume (PDF, Word, or Text)"
                name="resume"
                file={formData.resume}
                status={fileStatus.resume}
                accept=".pdf,.doc,.docx,.txt"
                onSelect={selectFile}
                onRemove={removeFile}
                helper="PDF, DOC, DOCX, TXT up to 10MB"
              />

              <FileUploadBox
                label="Personal Details (PDF, Word, or Text)"
                name="personalDetails"
                file={formData.personalDetails}
                status={fileStatus.personalDetails}
                accept=".pdf,.doc,.docx,.txt"
                onSelect={selectFile}
                onRemove={removeFile}
                helper="PDF, DOC, DOCX, TXT up to 10MB"
              />

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea id="notes" name="notes" rows="4" value={formData.notes} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" placeholder="Add any additional notes or comments about the lead..."></textarea>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/leads')}
                  disabled={isSubmitting}
                  className={`inline-flex justify-center py-2 px-4 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors ${
                    isSubmitting 
                      ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed' 
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors ${
                    isSubmitting 
                      ? 'bg-indigo-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    'Create Lead'
                  )}
                </button>
              </div>
            
            </div>
          </div>
        </form>
      </div>
      {errorModal && (
        <ErrorModal title={errorModal.title} message={errorModal.message} onClose={()=> setErrorModal(null)} />
      )}
    </div>
  );
}

export default CreateLeadNew;

function ErrorModal({ title, message, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl border border-red-200">
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xl">⚠️</div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="mt-2 whitespace-pre-line text-sm text-gray-700">{message}</p>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
